import {
    ChangeEvent,
    FC,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { CRM_OTHER_DETAILS_MAX_LENGTH } from '../../../common/config';
import { Dropdown } from '../../../components/Dropdown';
import { TemplateFlexGridContainer } from '../../../components/Grid';
import { Heading } from '../../../components/Heading';
import { InputGroup } from '../../../components/InputGroup';
import { PackagingImage } from '../../../components/PackagingImage';
import { Textarea } from '../../../components/Textarea';
import { useCountryMarketState } from '../../../controllers/CountryMarketController/CountryMarketState';
import {
    DropdownOption,
    DropdownOptionsWithUndefined,
} from '../../../lib/Dropdown/types';
import { noOp } from '../../../lib/utils';
import { StepProps } from '../../../types';
import { useBriefingState } from '../BriefingState';
import { packagingOptions } from './packagingTypes';

export const StepPackaging: FC<StepProps> = ({
  mandatoryFields,
  onValidate = noOp,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [
    { packaging, packagingOther, texture },
    { setPackaging, setPackagingOther },
  ] = useBriefingState();

  const [{ countryMarketConfig }] = useCountryMarketState();

  const [isPackagingOtherValid, setIsPackagingOtherValid] = useState(true);
  const isStepValid = useMemo(
    () => isPackagingOtherValid,
    [isPackagingOtherValid],
  );
  useEffect(() => onValidate(isStepValid), [isStepValid, onValidate]);

  const packagingOptionsByTexture: DropdownOption[] = useMemo(() => {
    let filteredPackagingOptions = packagingOptions.map((packagingOption) => {
      return {
        name: t(packagingOption.name),
        value: packagingOption.value,
        hidden:
          texture?.value &&
          !packagingOption.textures.includes(texture?.value.toString()),
      } as DropdownOption;
    });
    // If all packaging options are hidden (e.g. a texture is selected which doesn't have
    // any mapped packaging types) then show 'other' option
    if (filteredPackagingOptions.filter(({ hidden }) => !hidden).length === 0) {
      filteredPackagingOptions = [{ value: 'other', name: t('labels:other') }];
    }
    return filteredPackagingOptions;
  }, [texture, t]);

  useEffect(() => {
    if (
      packaging &&
      packagingOptionsByTexture.find(
        (packagingTextureItem) =>
          packagingTextureItem.value === packaging.value,
      )?.hidden
    ) {
      dispatch(setPackaging(null));
    }
  }, [dispatch, setPackaging, packaging, packagingOptionsByTexture]);

  const onPackagingChange = useCallback(
    (selectedPackaging: DropdownOptionsWithUndefined) => {
      // This will always be either a single DropdownOption or undefined
      dispatch(setPackaging((selectedPackaging as DropdownOption) || null));
      if (
        !selectedPackaging ||
        (!Array.isArray(selectedPackaging) &&
          selectedPackaging.value !== 'other')
      ) {
        dispatch(setPackagingOther(null));
      }
    },
    [dispatch, setPackaging, setPackagingOther],
  );

  const onPackagingOtherChange = (event: ChangeEvent) => {
    event.persist();
    dispatch(setPackagingOther((event.target as HTMLInputElement).value));
  };

  return (
    <TemplateFlexGridContainer.BriefingStep>
      <Heading
        level="h1"
        margin={0}
        text={t(`topics:packaging:heading:general`)}
        optionalField={mandatoryFields['packaging']?.length === 0}
      />
      <TemplateFlexGridContainer.BriefingStepBody>
        <div className="flex flex-grow flex-wrap xl:max-w-6xl">
          <div className="w-full md:w-5/12">
            <InputGroup label={t('labels:targetPackaging')}>
              <TemplateFlexGridContainer.BriefingStepSection>
                <Dropdown
                  initialSelection={packaging || undefined}
                  options={packagingOptionsByTexture || []}
                  error={packagingOptionsByTexture.length === 0}
                  onChange={onPackagingChange}
                />
                {packaging?.value === 'other' && (
                  <div>
                    <InputGroup
                      label={t('components:stepPackaging:otherPackaging')}
                    >
                      <div className="w-full __specification__ h-32">
                        <Textarea
                          placeholder={t('placeholders:textareaDefault')}
                          initialValue={packagingOther || undefined}
                          onChange={onPackagingOtherChange}
                          onValidate={setIsPackagingOtherValid}
                          maxLength={CRM_OTHER_DETAILS_MAX_LENGTH}
                        />
                      </div>
                    </InputGroup>
                  </div>
                )}
              </TemplateFlexGridContainer.BriefingStepSection>
            </InputGroup>
            <div className="flex flex-col-reverse sm:flex-row">
              <div className="mr-5">
                <p className="mt-12 font-regular">
                  {t('components:stepPackaging:packagingSamplePrompt')}
                </p>
                <p className="mt-8 font-medium">
                  {t('components:stepPackaging:addressLabel')}
                </p>
                <p className="whitespace-pre font-regular">
                  {countryMarketConfig?.labAddress}
                </p>
              </div>
              <div className="block md:hidden sm:mt-7">
                {packaging && (
                  <PackagingImage
                    isBig={true}
                    packagingValue={packaging.value as string}
                    packagingName={packaging.name as string}
                  />
                )}
              </div>
            </div>
          </div>
          <div className="w-full md:w-7/12">
            <div className="hidden md:block">
              {packaging && !packaging.hidden && (
                <PackagingImage
                  isBig={true}
                  packagingValue={packaging.value as string}
                  packagingName={packaging.name as string}
                />
              )}
            </div>
          </div>
        </div>
      </TemplateFlexGridContainer.BriefingStepBody>
    </TemplateFlexGridContainer.BriefingStep>
  );
};
